import React, { useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare } from '@fortawesome/free-solid-svg-icons';
import { NoRecordFound } from '../../../../components/Common/NoRecordFound';
import { AdminTableHeader } from '../../../../components/Admin';
import { BookGenerationRow } from '../../../../interfaces/bookGeneration.interface';
import BookCompleteBadge from '../../../../components/BookCompleteBadge';

interface BookGenerationsTableProps {
  books: BookGenerationRow[];
  onViewErrors?: (bookId: string) => void;
  onUpdatePageTotal?: (bookId: string, pageTotal: number) => Promise<void>;
}

const statusConfig: Record<string, { label: string; bg: string; text: string }> = {
  generating: { label: 'Generating', bg: 'bg-yellow-100', text: 'text-yellow-800' },
  complete: { label: 'Complete', bg: 'bg-green-100', text: 'text-green-800' },
  error: { label: 'Error', bg: 'bg-red-100', text: 'text-red-800' },
  story_ready: { label: 'Story Ready', bg: 'bg-blue-100', text: 'text-blue-800' },
  draft: { label: 'Draft', bg: 'bg-gray-100', text: 'text-gray-500' },
};

const formatDate = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const BookGenerationsTable: React.FC<BookGenerationsTableProps> = ({ books, onViewErrors, onUpdatePageTotal }) => {
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [editingBookId, setEditingBookId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const startEdit = (book: BookGenerationRow) => {
    setEditingBookId(book.bookId);
    setEditValue(String(book.pageTotal));
    setTimeout(() => inputRef.current?.select(), 0);
  };

  const cancelEdit = () => {
    setEditingBookId(null);
    setEditValue('');
  };

  const commitEdit = async (bookId: string) => {
    const val = parseInt(editValue, 10);
    if (isNaN(val) || val < 8) {
      cancelEdit();
      return;
    }
    setSaving(true);
    try {
      await onUpdatePageTotal?.(bookId, val);
    } finally {
      setSaving(false);
      setEditingBookId(null);
      setEditValue('');
    }
  };

  const handleCopyBookId = (bookId: string) => {
    navigator.clipboard.writeText(bookId).then(() => {
      setCopiedId(bookId);
      setTimeout(() => setCopiedId(null), 1500);
    });
  };

  const columns = [
    { key: 'bookId', label: 'Book ID' },
    { key: 'user', label: 'User' },
    { key: 'chars', label: 'Chars' },
    { key: 'textPages', label: 'Text Pages' },
    { key: 'totalPages', label: 'Total Pages' },
    { key: 'imgs', label: 'Imgs' },
    { key: 'errors', label: 'Errors' },
    { key: 'lastError', label: 'Last Error' },
    { key: 'created', label: 'Created' },
    { key: 'step', label: 'Step' },
    { key: 'status', label: 'Status' },
    { key: 'shippable', label: 'Shippable' },
    { key: 'actions', label: '' },
  ];

  return (
    <div className="w-full overflow-x-auto flex flex-col">
      <div>
        <table className="min-w-full border border-gray-300">
          <AdminTableHeader columns={columns} />
          <tbody>
            {books && books.length > 0 ? (
              books.map((book, index) => {
                const status = statusConfig[book.status] || statusConfig.draft;
                return (
                  <tr key={book.bookId} className={index % 2 === 0 ? 'bg-gray-100' : ''}>
                    {/* Book ID — click to copy */}
                    <td className="py-2 text-left px-4 border-b">
                      <button
                        onClick={() => handleCopyBookId(book.bookId)}
                        className="font-mono text-xs text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
                        title="Click to copy Book ID"
                      >
                        {book.bookId.substring(0, 12)}…
                      </button>
                      {copiedId === book.bookId && (
                        <span className="ml-1 text-xs text-green-600">✓</span>
                      )}
                    </td>

                    {/* User */}
                    <td className="py-2 text-left px-4 border-b text-sm">{book.email || '—'}</td>

                    {/* Characters Generated */}
                    <td className="py-2 text-left px-4 border-b text-sm">
                      {book.charactersGenerated}
                    </td>

                    {/* Text Pages (content pages count from pages map) */}
                    <td className="py-2 text-left px-4 border-b text-sm">
                      {book.illustrationTotal}
                    </td>

                    {/* Total Pages (physical pageTotal — editable) */}
                    <td className="py-2 text-left px-4 border-b text-sm">
                      {onUpdatePageTotal && editingBookId === book.bookId ? (
                        <input
                          ref={inputRef}
                          type="number"
                          min={8}
                          step={4}
                          value={editValue}
                          disabled={saving}
                          className="w-16 border border-blue-400 rounded px-1 py-0.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                          onChange={(e) => setEditValue(e.target.value)}
                          onBlur={() => commitEdit(book.bookId)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') commitEdit(book.bookId);
                            if (e.key === 'Escape') cancelEdit();
                          }}
                        />
                      ) : (
                        <span className="group inline-flex items-center gap-1">
                          {book.pageTotal}
                          {onUpdatePageTotal && (
                            <button
                              onClick={() => startEdit(book)}
                              className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-blue-600 transition-opacity"
                              title="Edit page total"
                            >
                              <FontAwesomeIcon icon={faPenToSquare} className="text-xs" />
                            </button>
                          )}
                        </span>
                      )}
                    </td>

                    {/* Imgs */}
                    <td className="py-2 text-left px-4 border-b text-sm">
                      <span className={book.imgsOk === book.illustrationTotal && book.illustrationTotal > 0 ? 'text-green-700 font-medium' : ''}>
                        {book.imgsOk}/{book.illustrationTotal}
                      </span>
                    </td>

                    {/* Error count */}
                    <td className="py-2 text-left px-4 border-b text-sm">
                      {book.errorCount > 0 ? (
                        <button
                          onClick={() => onViewErrors?.(book.bookId)}
                          className="bg-red-100 text-red-800 px-2 py-0.5 rounded text-xs font-medium hover:bg-red-200 cursor-pointer"
                          title="View errors for this book"
                        >
                          {book.errorCount}
                        </button>
                      ) : (
                        <span className="text-gray-400">0</span>
                      )}
                    </td>

                    {/* Last Error */}
                    <td className="py-2 text-left px-4 border-b text-sm max-w-[200px] truncate" title={book.lastError || ''}>
                      {book.lastError ? (
                        <span className="text-red-600 text-xs">{book.lastError}</span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>

                    {/* Created */}
                    <td className="py-2 text-left px-4 border-b text-sm whitespace-nowrap">
                      {formatDate(book.createdAt)}
                    </td>

                    {/* Step */}
                    <td className="py-2 text-left px-4 border-b text-sm">
                      {book.step}
                    </td>

                    {/* Status badge */}
                    <td className="py-2 text-left px-4 border-b">
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${status.bg} ${status.text}`}
                      >
                        {status.label}
                      </span>
                    </td>

                    {/* Print-aligned shippability (independent of the step-based status above) */}
                    <td className="py-2 text-left px-4 border-b">
                      <BookCompleteBadge isComplete={book.isShippable} reasons={book.incompleteReasons} />
                    </td>

                    {/* View link */}
                    <td className="py-2 text-left px-4 border-b text-sm">
                      <a
                        href={`/#/books/${book.bookId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 hover:underline text-xs font-medium"
                      >
                        View
                      </a>
                    </td>
                  </tr>
                );
              })
            ) : (
              <NoRecordFound colSpan={columns.length} />
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BookGenerationsTable;
